/**
 * useInboundCommsPopups — global inbound SMS / call / AI-booking toast layer
 * (TICK-73 + TICK-00214).
 *
 * Shows a small toast card to EVERY dispatcher (company-agnostic) when a new
 * inbound SMS, inbound call, or Olivia AI booking arrives, so nobody has to
 * sit on the Call Center Monitor page to notice new work.
 *
 *   - Inbound calls  : listen on the domain monitor WS (/ws/monitor/) for the
 *                      `call_notification_received` broadcast (same event the
 *                      monitor page reacts to). Reliable Twilio-era source.
 *   - Inbound SMS    : reuse the singleton /ws/sms/ trigger (useSmsWebSocket);
 *                      the WS payload is body-less, so a short preview is
 *                      fetched through the permission-enforced conversation API.
 *   - AI bookings    : same /ws/sms/ socket, event type `ai_booking_event`
 *                      ("New Booking from Olivia").
 *
 * SMS/call popups are gated by `dialer.monitor`. AI booking popups also show
 * for `dispatch.view` / `jobs.view` so office dispatchers without call-center
 * access still get Olivia alerts. A per-user preference (persisted in
 * localStorage, keyed by user id) toggles the popups and the sound.
 *
 * Singleton module state shared by the mounted component AND the App.vue user
 * menu (which only reads prefs / toggles). The component owns start()/stop().
 */
import { ref, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { smsInboxApi } from '@/api/communications'
import { fiveDigitCode, isSameFiveDigitBurst } from '@/utils/fiveDigitCode'
import { getAccessToken } from '@/auth/tokenManager'
import { playAlertSound } from '@/utils/alertSound'

const AUTO_DISMISS_MS = 20000
const MAX_POPUPS = 4

// ---- Singleton reactive state ------------------------------------------------
const popups = ref([])            // [{ id, type:'call'|'sms', title, name, number, preview, phone, callId }]
const enabled = ref(true)
const soundEnabled = ref(true)
let _prefsUserId = null
let _seq = 0

let _refCount = 0
let _unsubSms = null
let _monitorWs = null
let _monitorReconnect = null
let _monitorPingTimer = null
let _monitorClosedByUs = false
let _routePathGetter = () => ''

function _prefKey(userId) {
  return `inbound_popups_prefs_${userId || 'anon'}`
}

function _loadPrefs() {
  const authStore = useAuthStore()
  const userId = authStore.user?.id || null
  if (_prefsUserId === userId) return
  _prefsUserId = userId
  try {
    const raw = localStorage.getItem(_prefKey(userId))
    if (raw) {
      const parsed = JSON.parse(raw)
      enabled.value = parsed.enabled !== false      // default ON
      soundEnabled.value = parsed.sound !== false    // default ON
    } else {
      enabled.value = true
      soundEnabled.value = true
    }
  } catch {
    enabled.value = true
    soundEnabled.value = true
  }
}

function _savePrefs() {
  try {
    localStorage.setItem(_prefKey(_prefsUserId), JSON.stringify({
      enabled: enabled.value,
      sound: soundEnabled.value,
    }))
  } catch { /* ignore */ }
}

function setEnabled(val) {
  enabled.value = !!val
  _savePrefs()
  if (!enabled.value) {
    popups.value = []
    _disconnectMonitor()
  } else {
    _maybeConnectMonitor()
  }
}

function setSoundEnabled(val) {
  soundEnabled.value = !!val
  _savePrefs()
}

function toggleEnabled() { setEnabled(!enabled.value) }
function toggleSound() { setSoundEnabled(!soundEnabled.value) }

// ---- Permission / context helpers -------------------------------------------
function _hasPerm(code) {
  const authStore = useAuthStore()
  try { return !!authStore.hasPermission?.(code) } catch { return false }
}

function _canMonitor() {
  return _hasPerm('dialer.monitor')
}

function _canSeeAiBookingToasts() {
  return _canMonitor() || _hasPerm('dispatch.view') || _hasPerm('jobs.view')
}

// Mount the toast layer for call-center OR dispatch/jobs users (TICK-00214).
function _canSeeInboundPopups() {
  return _canSeeAiBookingToasts()
}

// Don't double-notify when the user is already watching the Call Center Monitor.
function _onMonitorPage() {
  return String(_routePathGetter() || '').startsWith('/call-center/monitor')
}

// ---- Popup lifecycle --------------------------------------------------------
function _pushPopup(popup) {
  if (!enabled.value) return
  const id = ++_seq
  const entry = { id, ...popup }
  popups.value = [entry, ...popups.value].slice(0, MAX_POPUPS)
  entry._timer = setTimeout(() => dismiss(id), AUTO_DISMISS_MS)
  if (soundEnabled.value) {
    try { playAlertSound('warning') } catch { /* ignore */ }
  }
}

function dismiss(id) {
  const p = popups.value.find(x => x.id === id)
  if (p && p._timer) clearTimeout(p._timer)
  popups.value = popups.value.filter(x => x.id !== id)
}

function _formatPhone(phone) {
  if (!phone) return ''
  const d = String(phone).replace(/\D/g, '').slice(-10)
  if (d.length !== 10) return phone
  return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

// ---- Inbound SMS ------------------------------------------------------------
async function _handleSmsEvent(msg) {
  if (!msg || msg.type !== 'sms_event') return
  if (msg.direction !== 'inbound') return
  // SMS toasts stay gated by dialer.monitor; dispatch-only users get AI booking only.
  if (!enabled.value || !_canMonitor() || _onMonitorPage()) return

  const phone = msg.customer_phone || ''
  const number = _formatPhone(phone) || phone || 'Unknown number'
  let name = msg.customer_name || ''
  let preview = ''
  let classification = null

  // Body-less WS: fetch a short preview through the permission-enforced API.
  // classify=true also returns the client-type (new/lead/existing/reengage) so
  // the toast can show a colored badge — same source as the Inbound Journal.
  try {
    if (phone) {
      // A 5-digit sender is not a customer. Skip classify so the toast does
      // not gain a New Lead chip.
      const { data } = await smsInboxApi.getConversation(
        phone,
        fiveDigitCode(phone) ? {} : { classify: true },
      )
      name = data?.customer_name || name
      classification = data?.classification || null
      const list = data?.messages || []
      for (let i = list.length - 1; i >= 0; i--) {
        if (list[i].direction === 'inbound' && (list[i].body)) { preview = list[i].body; break }
      }
    }
  } catch { /* preview is best-effort */ }

  const shown = preview ? (preview.length > 90 ? preview.slice(0, 90) + '…' : preview) : ''
  if (isSameFiveDigitBurst(popups.value, phone, shown)) return

  _pushPopup({
    type: 'sms',
    title: 'New SMS',
    name: name || 'Unknown number',
    number,
    preview: shown,
    phone,
    classification,
  })
}

// ---- AI Olivia booking (TICK-00214) -----------------------------------------
function _handleAiBookingEvent(msg) {
  if (!msg || msg.type !== 'ai_booking_event') return
  if (!enabled.value || !_canSeeAiBookingToasts()) return
  // Do NOT suppress on the Call Center Monitor — AI bookings are not shown
  // there the way SMS/calls are, and every online dispatcher must see them.

  const jobId = msg.job_id || null
  if (jobId && popups.value.some(p => p.type === 'ai_booking' && p.jobId === jobId)) return

  const phone = msg.customer_phone || ''
  const number = _formatPhone(phone) || phone || ''
  const name = msg.customer_name || ''
  const jobNumber = msg.job_number || jobId || ''
  const channel = (msg.channel || 'phone') === 'widget' ? 'website chat' : 'phone'
  const online = String(msg.created_via || '').toLowerCase() === 'online_booking'
  _pushPopup({
    type: 'ai_booking',
    title: online ? 'New online booking' : 'New Booking from Olivia',
    name: name || (jobNumber ? `Job #${jobNumber}` : (online ? 'Online booking' : 'AI booking')),
    number,
    preview: jobNumber
      ? `Job #${jobNumber} · ${online ? 'website / Google' : channel}`
      : (online
        ? 'Booked via the public online form'
        : `Automatically booked by Olivia (${channel})`),
    phone,
    jobId,
    sessionId: msg.session_id || null,
  })
}

// ---- Inbound calls (monitor WS) ---------------------------------------------
function _monitorUrl() {
  const loc = window.location
  const proto = loc.protocol === 'https:' ? 'wss:' : 'ws:'
  const token = getAccessToken() || ''
  return `${proto}//${loc.host}/ws/monitor/?token=${encodeURIComponent(token)}`
}

function _handleCallNotification(call) {
  if (!call || !enabled.value || _onMonitorPage()) return
  // Only inbound calls; outbound/created are ignored.
  if (call.direction && call.direction !== 'inbound') return
  const number = _formatPhone(call.phone_number || call.caller_id) || call.phone_number || 'Unknown number'
  const name = call.caller_name || call.customer_name || ''
  const callId = call.id || call.call_sid || call.sip_uuid || null
  // De-dupe: skip if a live popup already exists for this call.
  if (callId && popups.value.some(p => p.type === 'call' && p.callId === callId)) return
  _pushPopup({
    type: 'call',
    title: 'Incoming call',
    name: name || number,
    number,
    preview: call.customer_city ? `Existing customer · ${call.customer_city}` : '',
    callId,
  })
}

function _maybeConnectMonitor() {
  if (_monitorWs || !enabled.value || !_canMonitor()) return
  let url
  try { url = _monitorUrl() } catch { return }
  try {
    _monitorClosedByUs = false
    _monitorWs = new WebSocket(url)
  } catch (e) {
    console.warn('[InboundPopups] monitor WS ctor failed', e)
    return
  }
  _monitorWs.onopen = () => {
    console.log('[InboundPopups] monitor WS connected')
    if (_monitorPingTimer) clearInterval(_monitorPingTimer)
    _monitorPingTimer = setInterval(() => {
      try { if (_monitorWs?.readyState === WebSocket.OPEN) _monitorWs.send(JSON.stringify({ type: 'ping' })) } catch { /* ignore */ }
    }, 25000)
  }
  _monitorWs.onmessage = (evt) => {
    let data = null
    try { data = JSON.parse(evt.data) } catch { return }
    if (!data) return
    // Ignore initial_state / heavy payloads — we only care about new inbound calls.
    if (data.type === 'call_update' && data.action === 'call_notification_received') {
      _handleCallNotification(data.call || data)
    } else if (data.type === 'call_notification_received') {
      _handleCallNotification(data.call || data)
    }
  }
  _monitorWs.onclose = () => {
    if (_monitorPingTimer) { clearInterval(_monitorPingTimer); _monitorPingTimer = null }
    _monitorWs = null
    if (_monitorClosedByUs || !enabled.value || _refCount <= 0) return
    if (_monitorReconnect) return
    _monitorReconnect = setTimeout(() => {
      _monitorReconnect = null
      _maybeConnectMonitor()
    }, 5000)
  }
  _monitorWs.onerror = () => { /* onclose handles reconnect */ }
}

function _disconnectMonitor() {
  _monitorClosedByUs = true
  if (_monitorReconnect) { clearTimeout(_monitorReconnect); _monitorReconnect = null }
  if (_monitorPingTimer) { clearInterval(_monitorPingTimer); _monitorPingTimer = null }
  try { _monitorWs?.close() } catch { /* ignore */ }
  _monitorWs = null
}

// ---- Public start / stop (owned by the mounted component) -------------------
function start(smsOn, routePathGetter) {
  _refCount++
  _loadPrefs()
  if (typeof routePathGetter === 'function') _routePathGetter = routePathGetter
  if (_refCount > 1) return
  // SMS trigger (singleton socket, shared with ChatButton).
  if (typeof smsOn === 'function') {
    _unsubSms = smsOn((msg) => {
      _handleSmsEvent(msg)
      _handleAiBookingEvent(msg)
    })
  }
  _maybeConnectMonitor()
}

function stop() {
  _refCount = Math.max(0, _refCount - 1)
  if (_refCount > 0) return
  if (_unsubSms) { try { _unsubSms() } catch { /* ignore */ } _unsubSms = null }
  _disconnectMonitor()
  popups.value.forEach(p => { if (p._timer) clearTimeout(p._timer) })
  popups.value = []
}

export function useInboundCommsPopups() {
  return {
    popups,
    enabled,
    soundEnabled,
    canMonitor: computed(() => _canMonitor()),
    // Mount gate for App.vue — includes dispatch/jobs users (TICK-00214).
    canSeeInboundPopups: computed(() => _canSeeInboundPopups()),
    start,
    stop,
    dismiss,
    setEnabled,
    setSoundEnabled,
    toggleEnabled,
    toggleSound,
  }
}
